import * as RadixDialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import './Dialog.css';

export interface DialogProps {
  /** Controlled open state. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  /** Main body content. */
  children?: ReactNode;
  /** Footer action area (usually buttons). */
  actions?: ReactNode;
  /** Show the × close button (default true). */
  dismissible?: boolean;
  className?: string;
}

/**
 * Dialog — centered modal built on Radix Dialog (full a11y: focus trap,
 * ESC to close, aria-labelledby/describedby, backdrop click closes).
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  actions,
  dismissible = true,
  className,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fluent-dialog-overlay" />
        <RadixDialog.Content
          className={clsx('fluent-dialog', className)}
          onPointerDownOutside={(e) => !dismissible && e.preventDefault()}
          onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
        >
          {(title || description) && (
            <div className="fluent-dialog__head">
              {title && <RadixDialog.Title className="fluent-dialog__title">{title}</RadixDialog.Title>}
              {description && (
                <RadixDialog.Description className="fluent-dialog__description">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
          )}
          {children}
          {actions && <div className="fluent-dialog__actions">{actions}</div>}
          {dismissible && (
            <RadixDialog.Close asChild>
              <button type="button" className="fluent-dialog__close" aria-label="დახურე">
                <CloseIcon />
              </button>
            </RadixDialog.Close>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * Sheet — bottom-anchored mobile modal. Same API as Dialog, slides up from
 * the bottom edge with a drag-handle indicator. Same Radix a11y guarantees.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  actions,
  dismissible = true,
  className,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fluent-dialog-overlay" />
        <RadixDialog.Content
          className={clsx('fluent-sheet', className)}
          onPointerDownOutside={(e) => !dismissible && e.preventDefault()}
          onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
        >
          <div className="fluent-sheet__handle" aria-hidden="true" />
          {(title || description) && (
            <div className="fluent-dialog__head">
              {title && <RadixDialog.Title className="fluent-dialog__title">{title}</RadixDialog.Title>}
              {description && (
                <RadixDialog.Description className="fluent-dialog__description">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
          )}
          {children}
          {actions && <div className="fluent-dialog__actions">{actions}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
